import { ComponentProps, forwardRef } from 'react'

import {
  Breadcrumb,
  BreadcrumbType,
  CategorySub,
  FaqSettingMinimal,
  Hierarchy,
} from '@karakuri-web/faq-type'

import { BreadcrumbsNavigation, Search } from '../components'
import { useFaqData } from '../contexts/FaqDataContext'
import { useFaqRouting } from '../contexts/FaqRoutingContext'

/**
 * NaviWidget用の設定インターフェース
 */
export interface NaviWidgetSettings extends FaqSettingMinimal {
  words?: {
    search?: {
      resultMessage?: {
        success?: string | null
        error?: string | null
      } | null
    } | null
  } | null
}

interface Props<T extends NaviWidgetSettings = NaviWidgetSettings> {
  botId: string
  tenantId?: string | null
  breadcrumb: Breadcrumb
  breadcrumbType?: BreadcrumbType | null
  relatedCategories?: CategorySub[]
  hierarchies: Hierarchy[]
  searchWaitMs: number
  topTitle: string
  searchType: string
  data: { settingYaml: T }
}

/**
 * ナビゲーションウィジェット
 * パンくずリストと検索フォームを表示する
 */
export function NaviWidget<T extends NaviWidgetSettings = NaviWidgetSettings>({
  botId,
  tenantId,
  hierarchies,
  breadcrumb,
  breadcrumbType,
  relatedCategories = [],
  searchWaitMs,
  topTitle,
  searchType,
  data,
}: Props<T>) {
  const { Link, navigate } = useFaqRouting()
  const faqData = useFaqData()

  const handleSearched = (inputValue: string, result: { id: string; title: string }[]) => {
    faqData.sendSearchEvent({ inputValue, result })
    faqData.recordSearched({ botId, text: inputValue, tenantId })
  }

  const LinkComponent = forwardRef<HTMLAnchorElement, ComponentProps<typeof Link>>(
    function LinkComponent(props, ref) {
      const found = hierarchies.findIndex(h => h.link === props.to)
      const h = hierarchies.slice(0, found)
      const newProps: Omit<ComponentProps<typeof Link>, 'to'> = {}
      if (h.length > 0) {
        newProps.state = { hierarchies: h }
      }
      return <Link ref={ref} {...{ ...props, ...newProps }} />
    }
  )

  return (
    <div className="faq-navigation">
      <BreadcrumbsNavigation
        tags={[
          { link: '/', title: topTitle },
          ...relatedCategories
            .filter(x => !x.isParent)
            .map(c => {
              const title = c.title || ''
              return { link: `/category/${c.id}/`, title }
            }),
        ]}
        LinkComponent={LinkComponent}
        breadcrumbType={breadcrumbType}
        breadcrumb={breadcrumb}
        relatedCategories={relatedCategories}
        topTitle={topTitle}
      />

      <Search
        onClick={navigate}
        onSearched={handleSearched}
        searchWaitMs={searchWaitMs}
        search={async inputValue => {
          const result = await faqData.searchFaq({
            botId,
            inputValue: inputValue || '',
            searchType,
            tenantId,
          })
          return { data: result }
        }}
        resultMessage={{
          success: data.settingYaml?.words?.search?.resultMessage?.success || undefined,
          error: data.settingYaml?.words?.search?.resultMessage?.error || undefined,
        }}
      />
    </div>
  )
}
